import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiMic } from 'react-icons/fi';
import SEO from '../components/SEO';

const Search = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const [searchInput, setSearchInput] = useState(query);
  const [allResults, setAllResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [resultsToShow, setResultsToShow] = useState(20);
  const [isListening, setIsListening] = useState(false);
  const observerTarget = useRef(null);
  const recognitionRef = useRef(null);



  // Initialize Speech Recognition API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);
      };

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        let isFinal = false;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptSegment = event.results[i][0].transcript;
          transcript += transcriptSegment;
          isFinal = event.results[i].isFinal;
        }

        console.log('Transcript:', transcript, 'Final:', isFinal);

        if (transcript.trim()) {
          setSearchInput(transcript.trim());
          if (isFinal) {
            navigate(`/search?q=${encodeURIComponent(transcript.trim())}`);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [navigate]);

  useEffect(() => {
    setSearchInput(query);
    setResultsToShow(20);
  }, [query]);

  // Fetch search results from API
  useEffect(() => {
    if (!query.trim()) {
      setAllResults([]);
      setDisplayedResults([]);
      return;
    }

    const fetchSearchResults = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/search?q=${encodeURIComponent(query)}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }
        const result = await response.json();

        if (result.error || !result.data) {
          throw new Error('Invalid data structure from API');
        }

        // Map API response to video format
        const mappedResults = result.data.map(video => ({
          id: video.id,
          title: video.video_title,
          description: video.video_title,
          thumbnail: `https:${video.yt_thumb}`,
          poster: `https:${video.yt_thumb}`,
          duration: video.yt_length,
          rating: 4.5,
          category: 'Video',
          views: video.site_views?.toLocaleString() || '0',
        }));

        setAllResults(mappedResults);
        setDisplayedResults(mappedResults.slice(0, 20));
        setResultsToShow(20);
      } catch (err) {
        console.error("Search error:", err);
        setError(err.message);
        setAllResults([]);
        setDisplayedResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query]);

  // Lazy loading observer
  useEffect(() => {
    const currentObserverTarget = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && resultsToShow < allResults.length && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            const newCount = Math.min(resultsToShow + 20, allResults.length);
            setResultsToShow(newCount);
            setDisplayedResults(allResults.slice(0, newCount));
            setLoadingMore(false);
          }, 300);
        }
      },
      { threshold: 0.1 }
    );

    if (currentObserverTarget) {
      observer.observe(currentObserverTarget);
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
    };
  }, [resultsToShow, allResults, loadingMore]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchInput)}`);
    }
  };

  const handleClear = () => {
    setSearchInput('');
    navigate('/search');
  };

  const handleVoiceSearch = () => {
    if (!recognitionRef.current) {
      console.log('Speech Recognition not available');
      return;
    }

    if (isListening) {
      console.log('Stopping speech recognition');
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        console.log('Starting speech recognition');
        setIsListening(true);
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setIsListening(false);
      }
    }
  };

  return (
    <div className="bg-brand-dark min-h-screen text-white">
      <SEO
        title={query ? `Search Results for "${query}" - Christian Videos` : "Search - Christian Videos"}
        description="Search for your favorite Christian videos, sermons, and songs."
        url={`https://christianvideos.in/search${query ? `?q=${encodeURIComponent(query)}` : ''}`}
      />
      <div className="sticky top-0 z-30 bg-brand-dark/95 backdrop-blur-sm border-b border-gray-800 px-4 py-4">
        {/* Search Bar Section */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search videos, preachers, songs..."
                className="w-full pl-12 pr-20 py-3 bg-gray-800 text-white placeholder-gray-500 rounded-lg border border-gray-700 focus:border-gradient-accent focus:outline-none transition"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-gray-400 hover:text-white transition p-1"
                    title="Clear search"
                  >
                    <FiX size={20} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleVoiceSearch}
                  className={`p-2 rounded transition ${isListening
                    ? 'text-red-500 bg-red-500/30 animate-pulse'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                    }`}
                  title={isListening ? 'Stop listening' : 'Voice search (Click to speak)'}
                >
                  <FiMic size={20} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-accent text-white font-semibold rounded-lg hover:opacity-90 transition hidden sm:block"
            >
              Search
            </button>
          </form>
          {isListening && (
            <div className="mt-3 text-center">
              <p className="text-sm text-gradient-accent font-semibold animate-pulse mb-2">
                🎤 Listening... Speak now
              </p>
              <div className="flex justify-center gap-1">
                <div className="w-1 h-4 bg-gradient-accent rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-1 h-4 bg-gradient-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-1 h-4 bg-gradient-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
        {query ? (
          <>
            {/* Search Header */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
                Search Results for "<span className="text-gradient-accent">{query}</span>"
              </h1>
              {loading ? (
                <p className="text-xs sm:text-sm text-gray-400">Loading results...</p>
              ) : (
                <p className="text-xs sm:text-sm text-gray-400">Found {allResults.length} result{allResults.length !== 1 ? 's' : ''}</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-8 p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
                <p>Error: {error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gradient-accent"></div>
              </div>
            ) : (
              <>
                {/* Results Grid */}
                {allResults.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                      {displayedResults.map((video) => (
                        <div
                          key={video.id}
                          onClick={() => navigate(`/watch/${video.id}`)}
                          className="group cursor-pointer"
                        >
                          <div className="relative overflow-hidden rounded-lg bg-gray-800 aspect-video mb-2 sm:mb-3">
                            <img
                              src={video.poster}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-white/90 flex items-center justify-center">
                                  <svg className="w-5 sm:w-6 h-5 sm:h-6 text-brand-dark ml-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h3 className="font-semibold text-xs sm:text-sm line-clamp-2 group-hover:text-blue-400 transition-colors">
                            {video.title}
                          </h3>
                          <p className="text-xs text-gray-400 mt-1">
                            {video.views} Views
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Lazy Loading Indicator */}
                    {resultsToShow < allResults.length && (
                      <div ref={observerTarget} className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gradient-accent"></div>
                      </div>
                    )}

                    {/* End of Results */}
                    {resultsToShow >= allResults.length && allResults.length > 0 && (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-400">No more results to load</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-xl sm:text-2xl font-bold text-gray-400 mb-2">No results found</p>
                    <p className="text-xs sm:text-sm text-gray-500">Try searching with different keywords</p>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <FiSearch size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-xl sm:text-2xl font-bold text-gray-400 mb-2">Start Searching</p>
            <p className="text-xs sm:text-sm text-gray-500">Use the search bar above to find videos, preachers, and more</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
